import { Node, NodeProps } from "@xyflow/react"
import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import { cn } from "design-system/utils"
import { ensure } from "lopermut"
import { Check, CircleHelp, Plus, Rocket, Split, Trash, X } from "lucide-react"
import { Fragment, PropsWithChildren, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  WorkflowConditionDefinition,
  WorkflowConditionFieldEnum,
  CampaignSingleConditionRuleSchema,
  conditionValueKind,
} from "schema"
import { z } from "zod"
import { useWorkflowBuilder } from "../hooks/useWorkflowBuilder"
import { useConditionOperatorMap } from "../hooks/useConditionOperatorMap"
import { WorkflowNode } from "./WorkflowNode"

const MAX_CONDITIONS = 3

type ConditionBadgeProps = PropsWithChildren<{
  isOperator?: boolean
  empty: boolean
}>

export function ConditionBadge({
  children,
  isOperator = false,
  empty = false,
}: ConditionBadgeProps) {
  const isLongOperator = isOperator && typeof children === "string" && children.length > 1

  return (
    <div
      className={cn(
        "rounded-sm px-1 h-5.5 border border-border bg-secondary font-mono tracking-tighter font-normal text-[0.675rem] line-clamp-1 flex items-center justify-center",
        isOperator && "font-sans tracking-tighter text-destructive",
        isOperator && !isLongOperator && "text-[1.05rem] font-normal w-5.5",
        isOperator && isLongOperator && "w-auto",
        empty && !isOperator && "w-12",
      )}
      title={typeof children === "string" ? children : undefined}
    >
      {!empty && <span className={cn(isOperator && !isLongOperator && "mb-0.5")}>{children}</span>}
    </div>
  )
}

type ConditionNodeDescriptionProps = {
  conditions: z.infer<typeof CampaignSingleConditionRuleSchema>[] | undefined
  conditionOperatorMap: ReturnType<typeof useConditionOperatorMap>
}

function isLlmPromptCondition(condition: z.infer<typeof CampaignSingleConditionRuleSchema>) {
  return condition.field === WorkflowConditionFieldEnum.LLMPrompt
}

function ConditionNodeDescription({
  conditions,
  conditionOperatorMap,
}: ConditionNodeDescriptionProps) {
  const { t } = useTranslation()

  const condition = conditions?.[0]
  if (!condition || (condition.field === undefined && condition.operator === undefined)) {
    return <span className="text-base">{t("campaigns.condition.title")}</span>
  }

  return (
    <div className="flex flex-col gap-[3px] text-sm">
      {conditions?.slice(0, MAX_CONDITIONS).map((condition, index) => {
        const hasMoreThanMaxConditions = conditions.length > MAX_CONDITIONS
        const showCondition = hasMoreThanMaxConditions
          ? index < MAX_CONDITIONS - 1
          : index < MAX_CONDITIONS
        const showMoreConditions = hasMoreThanMaxConditions && index === 1
        const shouldShowLogic = index > 0 && condition.logic && showCondition
        const isLlmCondition = isLlmPromptCondition(condition)
        const operatorSymbol = condition.operator ? conditionOperatorMap[condition.operator] : ""
        const field = condition.field ? WorkflowConditionDefinition[condition.field] : null
        const isTranslatable = field && "isTranslatable" in field ? field.isTranslatable : false
        const value = (() => {
          if (!field || !condition.value) return condition.value
          if (isTranslatable) {
            return t(`campaigns.condition.values.${condition.field}.${condition.value}`)
          }
          if ("values" in field) {
            return field.values[condition.value as keyof typeof field.values]
          }
          return condition.value
        })()

        const valueKind =
          condition.field && condition.operator
            ? conditionValueKind(condition.field, condition.operator)
            : "none"

        return (
          <Fragment key={index}>
            {showCondition && (
              <div className="flex items-center gap-1 flex-wrap">
                {shouldShowLogic && (
                  <span className="text-[0.675rem] font-mono font-normal text-chart-primary">
                    {t(`campaigns.condition.logics.${condition.logic}`)}
                  </span>
                )}
                {isLlmCondition ? (
                  <>
                    <ConditionBadge empty={!condition.field}>
                      {t("campaigns.condition.fields.LLMPrompt")}
                    </ConditionBadge>
                    <ConditionBadge isOperator empty={!condition.operator}>
                      {operatorSymbol}
                    </ConditionBadge>
                    <ConditionBadge empty={!condition.config?.prompt}>
                      {condition.config?.prompt
                        ? t("campaigns.condition.llm.previewPlaceholder")
                        : ""}
                    </ConditionBadge>
                  </>
                ) : (
                  <>
                    <ConditionBadge empty={!condition.field}>
                      {condition.field ? t(`campaigns.condition.fields.${condition.field}`) : ""}
                    </ConditionBadge>
                    <ConditionBadge isOperator empty={!condition.operator}>
                      {operatorSymbol}
                    </ConditionBadge>
                    {valueKind !== "none" && (
                      <ConditionBadge empty={!value}>{value}</ConditionBadge>
                    )}
                  </>
                )}
              </div>
            )}
            {showMoreConditions && (
              <div className="flex items-center">
                <Badge variant="secondary" className="mt-1">
                  {t("shared.moreCount", { count: conditions.length - MAX_CONDITIONS + 1 })}
                </Badge>
              </div>
            )}
          </Fragment>
        )
      })}
    </div>
  )
}

export function ConditionNode({ data, type, ...props }: NodeProps<Node>) {
  const { t } = useTranslation()
  const conditionOperatorMap = useConditionOperatorMap()
  const { onAddConditionNode, onAddActionNode, onDeleteNode, setFocusedNode } = useWorkflowBuilder()
  const [openBranch, setOpenBranch] = useState<"Pass" | "Fail" | null>(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const { form } = useWorkflowBuilder()

  const nodeIndex = useMemo(
    () => ensure(form.watch("nodes")?.findIndex((node) => node.id === props?.id)),
    [form, props.id],
  )

  const conditions = form.watch(`nodes.${nodeIndex}.conditions`) as
    z.infer<typeof CampaignSingleConditionRuleSchema>[] | undefined
  const workflowNodes = form.watch("nodes") ?? []

  const isComplete = useMemo(() => {
    return (
      conditions &&
      conditions.length > 0 &&
      conditions.every((condition) => {
        if (isLlmPromptCondition(condition)) {
          return (
            condition.field &&
            condition.operator &&
            typeof condition.config?.prompt === "string" &&
            condition.config.prompt.trim().length > 0
          )
        }

        const needsValue = conditionValueKind(condition.field, condition.operator) !== "none"
        if (needsValue) return condition.field && condition.operator && condition.value
        return condition.field && condition.operator
      })
    )
    // NOTE: Trying to avoid unnecessary re-renders, but also catch all changes on the fields
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(conditions)])

  function handleAddConditionNode(e: React.MouseEvent<HTMLButtonElement>, branch: "Pass" | "Fail") {
    e.stopPropagation()
    setOpenBranch(null)
    setTimeout(() => {
      onAddConditionNode(props.id, "condition", branch)
    }, 100)
  }

  function handleAddActionNode(e: React.MouseEvent<HTMLButtonElement>, branch: "Pass" | "Fail") {
    e.stopPropagation()
    setOpenBranch(null)
    setTimeout(() => {
      onAddActionNode(props.id, "condition", branch)
    }, 100)
  }

  function onRequestDeleteNode(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation()
    setIsDeleteDialogOpen(true)
  }

  function onConfirmDeleteNode() {
    setIsDeleteDialogOpen(false)
    onDeleteNode(props.id)
  }

  function onCancelDeleteNode() {
    setIsDeleteDialogOpen(false)
    setFocusedNode(props.id)
  }

  return (
    <>
      <WorkflowNode
        {...props}
        data={data}
        type={type}
        labels={[
          <Badge key="condition" variant="outline">
            <Split className="w-4 h-4" />
            {t("campaigns.condition.label")}
          </Badge>,
        ]}
        description={
          <ConditionNodeDescription
            conditions={conditions}
            conditionOperatorMap={conditionOperatorMap}
          />
        }
        isComplete={isComplete}
        hasConditionOutputs
        addButton={
          <>
            {(["Pass", "Fail"] as const).map((branch) => (
              <Popover
                key={branch}
                open={openBranch === branch}
                onOpenChange={(open) => setOpenBranch(open ? branch : null)}
                modal
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    aria-label={t(`campaigns.condition.outputs.${branch}`)}
                    className={cn(
                      "group/output absolute z-10 h-8 rounded-full px-2 text-xs shadow-sm transition-all duration-200 hover:border-chart-primary",
                      branch === "Pass"
                        ? "-bottom-4 left-1/2 -translate-x-1/2"
                        : "right-0 top-1/2 translate-x-1/2 -translate-y-1/2",
                      !workflowNodes.some(
                        (node) => node.parentId === props.id && (node.branch ?? "Pass") === branch,
                      ) && "opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
                      openBranch === branch
                        ? "gap-1 border-chart-primary"
                        : "gap-0 group-hover:gap-1 focus-visible:gap-1",
                      openBranch === branch && "opacity-100",
                    )}
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      setOpenBranch(branch)
                    }}
                  >
                    <span className="relative block! size-4 shrink-0">
                      {branch === "Pass" ? (
                        <Check
                          className={cn(
                            "absolute inset-0 transition-opacity duration-200",
                            openBranch === branch
                              ? "opacity-0"
                              : "group-hover:opacity-0 group-focus-visible/output:opacity-0",
                          )}
                        />
                      ) : (
                        <X
                          className={cn(
                            "absolute inset-0 transition-opacity duration-200",
                            openBranch === branch
                              ? "opacity-0"
                              : "group-hover:opacity-0 group-focus-visible/output:opacity-0",
                          )}
                        />
                      )}
                      <Plus
                        className={cn(
                          "absolute inset-0 transition-opacity duration-200",
                          openBranch === branch
                            ? "opacity-100"
                            : "opacity-0 group-hover:opacity-100 group-focus-visible/output:opacity-100",
                        )}
                      />
                    </span>
                    <span
                      className={cn(
                        "overflow-hidden whitespace-nowrap transition-all duration-200",
                        openBranch === branch
                          ? "max-w-28 opacity-100"
                          : "max-w-0 opacity-0 group-hover:max-w-28 group-hover:opacity-100 group-focus-visible/output:max-w-28 group-focus-visible/output:opacity-100",
                      )}
                    >
                      {t(`campaigns.condition.outputs.${branch}`)}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent side="bottom" align="center" className="p-1">
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="ghost"
                      onClick={(e) => handleAddConditionNode(e, branch)}
                      className="justify-start items-center py-2 px-3 gap-2 min-w-32 w-auto h-auto"
                    >
                      <CircleHelp className="w-4 h-4 text-chart-primary" />
                      {t("campaigns.condition.label")}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={(e) => handleAddActionNode(e, branch)}
                      className="justify-start items-center py-2 px-3 gap-2 min-w-32 w-auto h-auto"
                    >
                      <Rocket className="w-4 h-4 text-chart-primary" />
                      {t("campaigns.action.label")}
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            ))}
          </>
        }
        deleteButton={
          <Button
            size="icon"
            variant="ghost"
            onClick={onRequestDeleteNode}
            className="absolute w-8 h-8 right-1 bottom-1 pointer-events-none opacity-0 transition-opacity duration-200 group-hover:pointer-events-auto group-hover:opacity-100 group-[.node-open]:pointer-events-auto group-[.node-open]:opacity-100 focus-visible:opacity-100"
          >
            <Trash className="w-4 h-4" />
          </Button>
        }
      />
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("campaigns.condition.deleteDialog.title")}</DialogTitle>
            <DialogDescription>
              {t("campaigns.condition.deleteDialog.description")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={onCancelDeleteNode}>
              {t("shared.cancel")}
            </Button>
            <Button variant="destructive" onClick={onConfirmDeleteNode}>
              {t("campaigns.condition.deleteDialog.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
